import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { useAuthHttp } from '@hooks';
import { CONFIRM_EMAIL_URL, FLOX_LOGO_BLUE_SRC } from '@constants';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../tailwindComponents/Button';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';

export function ConfirmEmail() {
  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const [searchParams] = useSearchParams();

  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useDocumentTitle('FLOX - Confirm Email');

  useEffect(() => {
    async function confirmEmail() {
      if (!searchParams.get('token') || !searchParams.get('email')) {
        setHasError(true);
        setIsLoading(false);
        return;
      }

      try {
        const { status } = await authHttp.post(CONFIRM_EMAIL_URL, {
          token: searchParams.get('token'),
          email: searchParams.get('email'),
        });

        if (status === 200) {
          setEmailConfirmed(true);
        } else {
          setHasError(true);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Email confirmation error:', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    }

    confirmEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className='flex min-h-screen w-full'>
      {/* Image Panel */}
      <section
        className='hidden md:block md:w-1/2 min-h-screen bg-cover bg-no-repeat bg-center transition-all duration-300'
        style={{ backgroundImage: 'url(/images/bg-login.png)' }}
      />

      {/* Content Panel */}
      <section className='flex-1 flex items-center justify-center bg-white px-4 py-12'>
        <div className='w-full max-w-md'>
          {/* Logo */}
          <div className='flex justify-center mb-8'>
            <img
              src={FLOX_LOGO_BLUE_SRC}
              alt='Flox logo'
              className='h-14 w-auto'
            />
          </div>

          {/* Card */}
          <div className='bg-white rounded-2xl shadow-lg border border-gray-100 p-8'>
            {/* Loading State */}
            {isLoading && (
              <div className='text-center'>
                <div className='flex justify-center mb-4'>
                  <div className='rounded-full bg-blue-100 p-3'>
                    <FloxSpinner size='md' />
                  </div>
                </div>
                <h1 className='text-2xl font-bold text-gray-900 mb-2'>
                  Confirming your email...
                </h1>
                <p className='text-gray-500'>
                  Please wait while we verify your email address.
                </p>
              </div>
            )}

            {/* Success State */}
            {!isLoading && emailConfirmed && (
              <div className='text-center'>
                <div className='flex justify-center mb-4'>
                  <div className='rounded-full bg-green-100 p-3'>
                    <CheckCircleIcon className='h-8 w-8 text-green-600' />
                  </div>
                </div>
                <h1 className='text-2xl font-bold text-gray-900 mb-2'>
                  Registration Complete!
                </h1>
                <p className='text-gray-500 mb-6'>
                  Thank you for confirming your email. Your account is now fully
                  activated and ready to use.
                </p>
                <Button
                  type='button'
                  intent='primary'
                  size='lg'
                  className='w-full'
                  label='Go to Dashboard'
                  onClick={() => navigate('/noticeboard')}
                />
              </div>
            )}

            {/* Error State */}
            {!isLoading && hasError && (
              <div className='text-center'>
                <div className='flex justify-center mb-4'>
                  <div className='rounded-full bg-yellow-100 p-3'>
                    <ExclamationTriangleIcon className='h-8 w-8 text-yellow-600' />
                  </div>
                </div>
                <h1 className='text-2xl font-bold text-gray-900 mb-2'>
                  Something went wrong
                </h1>
                <p className='text-gray-500 mb-6'>
                  We couldn&apos;t confirm your email address. The link may have
                  expired or is invalid.
                </p>
                <div className='space-y-3'>
                  <Button
                    type='button'
                    intent='primary'
                    size='lg'
                    className='w-full'
                    label='Try Again'
                    onClick={() => window.location.reload()}
                  />
                  <Button
                    type='button'
                    intent='secondary'
                    size='lg'
                    className='w-full'
                    label='Go to Login'
                    onClick={() => navigate('/login')}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className='mt-8 text-center'>
            <p className='text-sm text-gray-400'>
              Need help? Contact{' '}
              <a
                href='mailto:support@flox.is'
                className='text-blue-600 hover:underline'
              >
                support@flox.is
              </a>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
